import React, { useState, useEffect } from 'react';

import BluetoothIcon from '@mui/icons-material/Bluetooth';
import Battery20Icon from '@mui/icons-material/Battery20';
import Battery50Icon from '@mui/icons-material/Battery50';
import Battery80Icon from '@mui/icons-material/Battery80';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import {
  Box,
  Chip,
  Stack,
  Alert,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  DialogProps,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

import { useBleReader, BleReaderStatus } from 'src/hooks/useBleReader';

import { CONFIG } from 'src/environments/environment';

// Types
interface BluetoothReaderConnectionDialogProps extends DialogProps {
  open: boolean;
  onClose: VoidFunction;
  deviceName?: string;
}

export default function BluetoothReaderConnectionDialog({
  open,
  onClose,
  deviceName,
  ...dialogProps
}: BluetoothReaderConnectionDialogProps) {
  const {
    status,
    reader,
    battery,
    initializeTerminal,
    checkConnectedReader,
    discoverReaders,
    connectReader,
    disconnectReader,
  } = useBleReader();

  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [discoveredReaders, setDiscoveredReaders] = useState<any[]>([]);

  // Attempt connection when dialog opens
  useEffect(() => {
    if (open && status === BleReaderStatus.NOT_INITIALIZED) {
      handleConnect();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async () => {
    setIsConnecting(true);
    setConnectionError(null);

    try {
      // Initialize terminal
      const initialized = await initializeTerminal();
      if (!initialized) {
        throw new Error('Failed to initialize terminal');
      }

      // Check for existing connection
      const existingReader = await checkConnectedReader();
      if (existingReader) {
        setIsConnecting(false);
        return;
      }

      // Discover readers
      const locationId = CONFIG.VITE_DEFAULT_TERMINAL_READER_LOCATION_ID;
      const readers = await discoverReaders(locationId);
      setDiscoveredReaders(readers);

      if (readers.length === 0) {
        throw new Error('No Bluetooth readers found. Please ensure your reader is powered on and nearby.');
      }

      // Connect to first reader
      const connected = await connectReader(readers[0]);
      if (!connected) {
        throw new Error('Failed to connect to reader');
      }

      setIsConnecting(false);
    } catch (error: any) {
      console.error('BLE Reader connection error:', error);
      setConnectionError(error.message || 'Failed to connect to reader');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectReader();
      setConnectionError(null);
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const getBatteryIcon = () => {
    if (!battery.level) return <BatteryFullIcon />;

    if (battery.isCharging) return <BatteryChargingFullIcon color="success" />;

    if (battery.level < 20) return <Battery20Icon color="error" />;
    if (battery.level < 50) return <Battery50Icon color="warning" />;
    if (battery.level < 80) return <Battery80Icon color="info" />;

    return <BatteryFullIcon color="success" />;
  };

  const getStatusIcon = () => {
    if (status === BleReaderStatus.CONNECTED && reader) {
      return <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />;
    }
    if (connectionError) {
      return <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main' }} />;
    }
    return <BluetoothIcon sx={{ fontSize: 40, color: 'primary.main' }} />;
  };

  const getStatusColor = () => {
    if (status === BleReaderStatus.CONNECTED) return 'success';
    if (status === BleReaderStatus.ERROR || connectionError) return 'error';
    if (isConnecting) return 'info';
    return 'default';
  };

  const getStatusText = () => {
    switch (status) {
      case BleReaderStatus.INITIALIZING:
        return 'Initializing...';
      case BleReaderStatus.DISCOVERING:
        return 'Searching for readers...';
      case BleReaderStatus.CONNECTING:
        return 'Connecting...';
      case BleReaderStatus.CONNECTED:
        return 'Connected';
      case BleReaderStatus.ERROR:
        return 'Connection Error';
      case BleReaderStatus.DISCONNECTED:
        return 'Disconnected';
      default:
        return 'Not Connected';
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
      {...dialogProps}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        Connect Bluetooth Reader
      </DialogTitle>

      <DialogContent sx={{ py: 4 }}>
        <Stack spacing={3} alignItems="center">
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: (theme) => {
                if (status === BleReaderStatus.CONNECTED) return theme.palette.success.lighter;
                if (connectionError) return theme.palette.error.lighter;
                return theme.palette.primary.lighter || theme.palette.action.hover;
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {isConnecting ? (
              <CircularProgress size={40} />
            ) : (
              getStatusIcon()
            )}
          </Box>

          {deviceName && (
            <Typography variant="body1" textAlign="center" color="text.secondary">
              Device &quot;{deviceName}&quot; has been created successfully.
            </Typography>
          )}

          <Chip
            label={getStatusText()}
            color={getStatusColor() as any}
            variant={status === BleReaderStatus.CONNECTED ? "filled" : "outlined"}
          />

          {/* Connected Reader Info */}
          {reader && status === BleReaderStatus.CONNECTED && (
            <Box
              sx={{
                width: '100%',
                p: 2,
                borderRadius: 1,
                bgcolor: (theme) => theme.palette.success.lighter,
                border: 1,
                borderColor: 'success.main',
              }}
            >
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="success.dark" fontWeight={600}>
                  Reader Connected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Serial: {reader.serialNumber}
                </Typography>
                {(reader as any).label && (
                  <Typography variant="body2" color="text.secondary">
                    Label: {(reader as any).label}
                  </Typography>
                )}

                {/* Battery Status */}
                {battery.level !== null && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getBatteryIcon()}
                    <Typography variant="body2" color="text.secondary">
                      {battery.level}%
                      {battery.isCharging && ' (Charging)'}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}

          {/* Error Message */}
          {connectionError && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {connectionError}
            </Alert>
          )}

          {/* Discovery Info */}
          {discoveredReaders.length > 0 && !reader && !connectionError && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Found {discoveredReaders.length} reader(s). Connecting to first available reader...
            </Typography>
          )}

          {/* Instructions */}
          {!reader && !isConnecting && !connectionError && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Make sure your Bluetooth reader is powered on and nearby.
              The reader will automatically connect when you start a payment.
            </Typography>
          )}

          {/* Low Battery Warning */}
          {battery.status === 'low' && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              Reader battery is low. Consider charging soon.
            </Alert>
          )}

          {battery.status === 'critical' && (
            <Alert severity="error" sx={{ width: '100%' }}>
              Reader battery is critically low! Connect charger immediately.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Stack spacing={2} sx={{ width: '100%' }}>
          {/* Action Buttons */}
          {status === BleReaderStatus.CONNECTED ? (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                fullWidth
              >
                Disconnect Reader
              </Button>
              <Button
                variant="contained"
                onClick={onClose}
                fullWidth
              >
                Done
              </Button>
            </>
          ) : (
            <>
              {connectionError && (
                <Button
                  variant="outlined"
                  onClick={handleConnect}
                  disabled={isConnecting}
                  fullWidth
                >
                  Retry Connection
                </Button>
              )}
              <Button
                variant="contained"
                onClick={onClose}
                fullWidth
              >
                {connectionError ? 'Close' : 'Setup Later'}
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
